/**
 * Campaign Commander page — Patch 36.2.2.
 *
 * A manual-mode workspace no longer disappears into Settings. The user is
 * told what is required, given one clear action, and returned directly to
 * campaign creation after the mode change succeeds.
 */
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { useSetOperatingMode } from "@/entities/workspace/hooks";
import type { Workspace } from "@/entities/workspace/model";
import { CampaignCommanderWizard } from "@/features/campaign-commander";
import { Button } from "@/shared/ui/button";
import { WorkspaceGate } from "@/widgets/WorkspaceGate/WorkspaceGate";

export default function CampaignCommanderPage(): JSX.Element {
  return (
    <WorkspaceGate>
      {(workspace) =>
        workspace.settings.operating_mode === "autopilot" ? (
          <CampaignCommanderWizard />
        ) : (
          <EnableGuidedCampaigns workspace={workspace} />
        )
      }
    </WorkspaceGate>
  );
}

function EnableGuidedCampaigns({ workspace }: { workspace: Workspace }): JSX.Element {
  const mutation = useSetOperatingMode(workspace.id);

  const enableAndContinue = () => {
    mutation.mutate("autopilot", {
      onSuccess: () => {
        toast.success("Guided campaigns are ready", {
          description: "Tell Orkexra what you want to accomplish. You will review everything before anything is published.",
        });
      },
      onError: () => {
        toast.error("We couldn't enable guided campaigns", {
          description: "Nothing changed. Please try again.",
        });
      },
    });
  };

  return (
    <main className="mx-auto flex w-full max-w-3xl flex-1 items-center px-4 py-12 md:px-6">
      <section
        data-testid="campaign-mode-gate"
        className="w-full rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-6 md:p-8"
      >
        <div className="mb-5 grid h-11 w-11 place-items-center rounded-lg bg-[var(--md-ink-2)] text-[var(--md-amber)]">
          <Sparkles className="h-5 w-5" aria-hidden />
        </div>
        <p className="md-eyebrow">One quick setup</p>
        <h1 className="mt-2 text-2xl font-semibold text-[var(--md-ivory-0)]">
          Let Orkexra build the campaign with you
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-[var(--md-fog)]">
          Guided Campaign Mode turns your goal into a complete campaign plan and draft assets. You remain in control: nothing is published without your approval.
        </p>
        <Button
          type="button"
          data-testid="campaign-enable-guided-mode"
          className="mt-6"
          disabled={mutation.isPending}
          onClick={enableAndContinue}
        >
          {mutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}
          Enable Guided Mode and Continue
        </Button>
        <p className="mt-3 text-xs text-[var(--md-fog)]">
          You can change this later in Workspace Settings. No existing work is deleted.
        </p>
      </section>
    </main>
  );
}
